import { useCallback, useEffect, useMemo, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '../store';
import type { UserClinicRole } from '../store/slices/authSlice';
import { getAgencyClinicDisplayName } from '../utils/agencyClinicDisplayName';

export const ALL_CLINICS = '__all__';

export interface AccessibleClinic {
  id: string;
  name: string;
  clinic_source: string | null;
  is_kinnser?: boolean;
  clinic_key?: string | null;
  branches?: Array<{ [key: string]: unknown }> | null;
  city?: string | null;
  state?: string | null;
  /** Present when the user has a clinic-scoped role at this clinic. */
  userClinic?: UserClinicRole;
}

export interface UseAccessibleClinicsResult {
  /** Clinics the user may access, resolved to display names and sorted. */
  clinics: AccessibleClinic[];
  /** Currently selected clinic id, or `ALL_CLINICS` for no filter. */
  selectedClinicId: string;
  setSelectedClinicId: (id: string) => void;
  /** True when no specific clinic is selected. */
  isAllClinics: boolean;
  /**
   * Whether to render a clinic picker at all.
   *
   * False when the user can reach one clinic or none: a picker that can only
   * ever resolve to a single value is noise, and leaving it out removes a
   * decision from surfaces used by single-clinic clinicians. Callers should hide
   * their picker on false rather than disabling it — `selectedClinicId` stays
   * `ALL_CLINICS`, which the backend already clamps to the accessible set, so
   * the resulting query is identical either way.
   */
  shouldShowPicker: boolean;
  /**
   * True once the agency clinic list has been fetched at least once (whether
   * or not it succeeded). Before that, `clinics` is empty and
   * `shouldShowPicker` is false for everyone, so layout that depends on the
   * clinic count should treat the count as unknown rather than as one.
   */
  clinicsLoaded: boolean;
}

/**
 * Clinics the current user may filter by (Patient Manager, Hotbox, and friends).
 *
 * Access itself is enforced server-side: `agencyClinics` comes
 * from `GET /agency-clinics/`, which the ORM clinic filter narrows to the
 * user's `user_clinics` rows. This hook therefore does not decide access — it
 * presents what the server returned. Do not re-derive access from
 * `user.user_clinics` here; that list reflects clinic-scoped *roles*, which is a
 * narrower concept than access.
 *
 * `requireAssignment` still narrows to clinics where the user holds a
 * clinic-scoped role, which Hotbox wants for its "my work" framing. That is a
 * role filter, not an access filter.
 *
 * Pass `persistKey` to remember the selection across sessions for multi-clinic
 * users, scoped to that key so two surfaces don't fight over one value.
 */
export function useAccessibleClinics(options?: {
  requireAssignment?: boolean;
  persistKey?: string;
}): UseAccessibleClinicsResult {
  const requireAssignment = options?.requireAssignment ?? false;
  const persistKey = options?.persistKey;

  const {
    user,
    agencyClinics,
    agencyClinicsLastFetched,
    fetchAgencyClinics,
    persistedSelection,
    setClinicFilterSelection,
  } = useStore(
    useShallow(state => ({
      user: state.user,
      agencyClinics: state.agencyClinics,
      agencyClinicsLastFetched: state.agencyClinicsLastFetched,
      fetchAgencyClinics: state.fetchAgencyClinics,
      persistedSelection: persistKey
        ? state.clinicFilterSelections[persistKey]
        : undefined,
      setClinicFilterSelection: state.setClinicFilterSelection,
    }))
  );

  useEffect(() => {
    fetchAgencyClinics(user?.agency?.id);
  }, [fetchAgencyClinics, user?.agency?.id]);

  const clinics = useMemo((): AccessibleClinic[] => {
    const userClinicsById = new Map(
      (user?.user_clinics ?? []).map(uc => [uc.clinic_id, uc])
    );
    const totalClinicCount = agencyClinics.length;
    return agencyClinics
      .map(clinic => ({
        id: clinic.id,
        name: getAgencyClinicDisplayName(clinic, { totalClinicCount }),
        clinic_source: clinic.clinic_source ?? null,
        is_kinnser: clinic.is_kinnser,
        clinic_key: clinic.clinic_key,
        branches: clinic.branches,
        city: clinic.city,
        state: clinic.state,
        userClinic: userClinicsById.get(clinic.id),
      }))
      .filter(clinic => !requireAssignment || clinic.userClinic != null)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [user?.user_clinics, agencyClinics, requireAssignment]);

  const [selectedClinicId, setSelectedClinicIdState] = useState<string>(
    persistedSelection ?? ALL_CLINICS
  );

  const setSelectedClinicId = useCallback(
    (id: string) => {
      setSelectedClinicIdState(id);
      if (persistKey) {
        setClinicFilterSelection(persistKey, id);
      }
    },
    [persistKey, setClinicFilterSelection]
  );

  useEffect(() => {
    // Only reconcile once clinics have loaded. Running against an empty list
    // would discard a valid persisted selection on every mount, before the
    // fetch resolves.
    if (clinics.length === 0) {
      return;
    }
    if (
      selectedClinicId !== ALL_CLINICS &&
      !clinics.some(c => c.id === selectedClinicId)
    ) {
      setSelectedClinicId(ALL_CLINICS);
    }
  }, [clinics, selectedClinicId, setSelectedClinicId]);

  const isAllClinics = selectedClinicId === ALL_CLINICS;

  return {
    clinics,
    selectedClinicId,
    setSelectedClinicId,
    isAllClinics,
    shouldShowPicker: clinics.length > 1,
    clinicsLoaded: agencyClinicsLastFetched != null,
  };
}
